/**
 * hebrewParsing.js
 *
 * Decodes OSHB (Open Scriptures Hebrew Bible) morphology codes into human-readable labels.
 *
 * Code format: {lang}[prefix/]{pos}{features...}
 *   lang = H (Hebrew) or A (Aramaic)
 *   pos  = V(erb) N(oun) P(ronoun) A(djective) D(adverb) R(preposition)
 *          C(onjunction) T(particle) I(nterjection) S(uffix)
 *
 * Examples:
 *   HC/Vqw3ms → prefixes:['Conjunction']  pos:'Verb'  details:['Qal','Wayyiqtol','3rd person','Masculine','Singular']
 *   HR/Ncmpc  → prefixes:['Preposition']  pos:'Noun'  details:['Common','Masculine','Plural','Construct']
 *   HNcmsa    → prefixes:[]               pos:'Noun'  details:['Common','Masculine','Singular','Absolute']
 */

const POS = {
  V: 'Verb',         N: 'Noun',       P: 'Pronoun',    A: 'Adjective',
  D: 'Adverb',       R: 'Preposition', C: 'Conjunction', T: 'Particle',
  I: 'Interjection', S: 'Suffix',     E: 'Exhortation',
};

const VERB_STEM = {
  q: 'Qal',    N: 'Niphal',      p: 'Piel',     P: 'Pual',
  h: 'Hiphil', H: 'Hophal',      t: 'Hithpael',
  o: 'Polel',  O: 'Polal',       r: 'Hithpolel',
  m: 'Poel',   M: 'Poal',        Q: 'Qal passive',
  D: 'Nithpael', i: 'Pilpel',    u: 'Hothpaal',
  c: 'Tiphil', v: 'Hishtaphel',  w: 'Nithpalel',
};

const VERB_ASPECT = {
  p: 'Perfect',              i: 'Imperfect',
  w: 'Wayyiqtol',            q: 'Weqatal',
  h: 'Cohortative',          j: 'Jussive',
  v: 'Imperative',           r: 'Participle',
  s: 'Passive participle',   a: 'Infinitive absolute',
  c: 'Infinitive construct',
};

const PERSON = { '1': '1st person', '2': '2nd person', '3': '3rd person' };
const GENDER = { m: 'Masculine', f: 'Feminine', b: 'Both', c: 'Common', e: 'Either' };
const NUMBER = { s: 'Singular', p: 'Plural', d: 'Dual' };

const NOUN_TYPE  = { c: 'Common', p: 'Proper', g: 'Gentilic' };
const NOUN_STATE = { a: 'Absolute', c: 'Construct', d: 'Determined' };

const PRON_TYPE = {
  p: 'Personal', d: 'Demonstrative', r: 'Relative',
  f: 'Reflexive', i: 'Interrogative', x: 'Indefinite',
};

function decodeSegment(gramCode) {
  if (!gramCode) return { pos: '—', details: [] };

  const posChar = gramCode[0];
  const pos = POS[posChar];
  if (!pos) return { pos: gramCode, details: [] };

  const r = gramCode.slice(1);
  const details = [];

  if (posChar === 'V') {
    if (VERB_STEM[r[0]])   details.push(VERB_STEM[r[0]]);
    if (VERB_ASPECT[r[1]]) details.push(VERB_ASPECT[r[1]]);
    if (PERSON[r[2]])      details.push(PERSON[r[2]]);
    if (GENDER[r[3]])      details.push(GENDER[r[3]]);
    if (NUMBER[r[4]])      details.push(NUMBER[r[4]]);
  } else if (posChar === 'N' || posChar === 'A') {
    if (NOUN_TYPE[r[0]])  details.push(NOUN_TYPE[r[0]]);
    if (GENDER[r[1]])     details.push(GENDER[r[1]]);
    if (NUMBER[r[2]])     details.push(NUMBER[r[2]]);
    if (NOUN_STATE[r[3]]) details.push(NOUN_STATE[r[3]]);
  } else if (posChar === 'P') {
    if (PRON_TYPE[r[0]]) details.push(PRON_TYPE[r[0]]);
    if (PERSON[r[1]])    details.push(PERSON[r[1]]);
    if (GENDER[r[2]])    details.push(GENDER[r[2]]);
    if (NUMBER[r[3]])    details.push(NUMBER[r[3]]);
  }

  return { pos, details };
}

/**
 * Decode an OSHB morphology code string.
 * @param {string} code  e.g. "HC/Vqw3ms", "HNcmsa"
 * @returns {{ pos: string, details: string[], prefixes: string[] }}
 */
export function decodeParsingCode(code) {
  if (!code) return { pos: '—', details: [], prefixes: [] };

  const segments = code.split('/');
  const prefixes = [];
  let main = { pos: '—', details: [] };

  segments.forEach((seg, i) => {
    const gramCode = (seg[0] === 'H' || seg[0] === 'A') ? seg.slice(1) : seg;
    const result   = decodeSegment(gramCode);
    if (i < segments.length - 1) prefixes.push(result.pos);
    else main = result;
  });

  return { ...main, prefixes };
}

export function shortParsing(code) {
  const { pos, details, prefixes } = decodeParsingCode(code);
  const label = prefixes.length ? `[${prefixes.join('+')}] ${pos}` : pos;
  return details.length ? `${label} · ${details.join(' · ')}` : label;
}
